import * as React from 'react';
import { DataTest } from '../../utilities/testAutomation';
import { analytics, formLinks } from '../../content/content';
import Button, { ButtonProps } from '../Button/Button';

type TileIcon = 'file' | 'contact' | 'print';

const ICON_SOURCES: { [key in TileIcon]: string } = {
  file: `${process.env.PUBLIC_URL || ''}/assets/images/file.svg`,
  contact: `${process.env.PUBLIC_URL || ''}/assets/images/contact.svg`,
  print: `${process.env.PUBLIC_URL || ''}/assets/images/print.svg`,
};

export interface TileItem {
  id?: string;
  href?: string;
  role?: string;
  linkClassName?: string;
  tileClassName?: string;
  iconClassName?: string;
  icon?: TileIcon;
  title: string;
  description?: string;
  description2?: string;
  button?: ButtonProps;
  dataTestTag?: string;
  planType?: string;
  onClick?: () => void;
}

export interface TilesProps extends DataTest {
  id?: string;
  className?: string;
  items: TileItem[];
  planType: string;
  single?: boolean;
}

const joinClassNames = (...classNames: Array<string | undefined | false>) =>
  classNames.filter(Boolean).join(' ');

const TileContent = ({
  iconClassName,
  icon,
  title,
  description,
  description2,
  button,
  planType,
}: TileItem) => (
  <>
    <div className={joinClassNames('tiles__icon', iconClassName)}>
      {icon && (
        <img
          className="tiles__icon-image"
          src={ICON_SOURCES[icon]}
          alt=""
          aria-hidden="true"
        />
      )}
    </div>
    <div className="tiles__content extraFunctionContent">
      <div className="tiles__title">{title}</div>
      {description && <div className="tiles__description">{description}</div>}
      {description2 && <div className="tiles__description -margin">{description2}</div>}
      {button && (
        <Button
          style={{ marginTop: '17px', justifySelf: 'flex-start' }}
          name={button.name}
          link={planType && formLinks[planType]}
          analytics={button.analytics}
          dataTest={button.dataTest}
        />
      )}
    </div>
  </>
);

const Tiles = ({
  id,
  className,
  items,
  planType,
  dataTest,
  single,
}: TilesProps) => (
  <div id={id} className={joinClassNames('tiles row-fluid', className)}>
    <div className="tiles__row" id="extraFunctions">
      {items.map((item, index) => {
        const key = item.id || item.title || `tile-${index}`;
        const tileClassName = joinClassNames(
          'tiles__item extraFunction',
          'tiles__container',
          item.onClick ? 'tiles__pointer' : '',
          item.tileClassName
        );

        return (
          <div style={{ width: single ? '50%' : '100%', flex: single ? 'none' : 1 }} className="tiles__cell" key={key} {...(item.dataTestTag ? { 'data-test': item.dataTestTag } : {})}>
            {item.href ? (
              <a
                className={joinClassNames('tiles__link bookmark', item.linkClassName)}
                href={item.href}
                role={item.role || 'button'}
                {...dataTest}
              >
                <div className={tileClassName}>
                  <TileContent {...item} planType={planType} />
                </div>
              </a>
            ) : (
              <div onClick={item?.onClick} className={tileClassName} role={item.role || 'group'} {...dataTest}>
                <TileContent {...item} planType={planType} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export const importantInfoOptionsTiles: TileItem[] = [
  {
    icon: 'print',
    title: 'Print these results',
    description: 'Print this page for your records',
    onClick: () => window.print(),
  },
];

export const retirementOptionsTiles: TileItem[] = [
  {
    icon: 'file',
    title: 'What next?',
    description: 'To change your current income amount from your drawdown plan or to take a lump sum, download, complete and post the change of income form to us.',
    button: {
      name: 'Download form',
      analytics: {
        id: analytics.buttonsId,
        data: analytics.download,
      },
      dataTest: {
        'data-test': 'soi-next-steps-button-download-form',
      },
    },
  },
  {
    id: 'investmentBookmark',
    tileClassName: 'bookmarkPage',
    icon: 'contact',
    title: 'Talk to us',
    description: 'If you\'d like to speak to someone about your options, we\'re here to help. We can\'t give you advice or make your decision for you but we\'re happy to talk through your options and their possible implications.',
    description2: 'Call us on 0808 234 2372. We\'re here 9am-5pm, Monday to Friday. We might record your call to make sure our service is up to standard.',
  },
];

export default Tiles;



// WEBPACK FOOTER //
// ./src/components/Tiles/Tiles.tsx